/**
 * Generates a URL-safe key from a label
 * @param label - The human-readable label
 * @returns A lowercase, snake_case key suitable for database storage
 * @example generateKeyFromLabel("Driver Type") => "driver_type"
 */
export const generateKeyFromLabel = (label: string): string => {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Validates that a label will generate a non-empty key
 * @param label - The human-readable label
 * @returns True if the label will generate a valid key
 */
export const isValidLabelForKey = (label: string): boolean => {
  return generateKeyFromLabel(label).length > 0
}

/**
 * Generates a URL-safe value from an option label
 * @param label - The option label
 * @returns An uppercase, SNAKE_CASE value
 * @example generateValueFromLabel("Company Driver") => "COMPANY_DRIVER"
 */
export const generateValueFromLabel = (label: string): string => {
  return label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Validates that an option label will generate a non-empty value
 * @param label - The option label
 * @returns True if the label will generate a valid value
 */
export const isValidLabelForValue = (label: string): boolean => {
  return generateValueFromLabel(label).length > 0
}

/**
 * Gets a user-friendly label for a field type
 * @param fieldType - The CustomFieldType enum value
 * @returns A human-readable label
 */
export const getFieldTypeLabel = (fieldType: string): string => {
  const labels: Record<string, string> = {
    STRING: 'Text',
    BOOLEAN: 'Yes/No',
    DATE: 'Date',
    SINGLE_SELECT: 'Single Select',
    MULTI_SELECT: 'Multi Select',
  }
  return labels[fieldType] || fieldType
}

/**
 * Checks if a field type can be marked as searchable
 * @param fieldType - The CustomFieldType enum value
 * @returns True if the field type supports searching
 */
export const canBeSearchable = (fieldType: string): boolean => {
  return ['BOOLEAN', 'DATE', 'SINGLE_SELECT', 'MULTI_SELECT'].includes(
    fieldType
  )
}

/**
 * Checks if a field type requires options
 * @param fieldType - The CustomFieldType enum value
 * @returns True if the field type requires options
 */
export const requiresOptions = (fieldType: string): boolean => {
  return ['SINGLE_SELECT', 'MULTI_SELECT'].includes(fieldType)
}
